/** @file The one mesh topology rule (W2-32): validateDoc and the mesh commands both apply it. */

/** A mesh topology as checked: uvs (2 per vertex), positions (rigid vertices or skinned bind vertices), index lists. */
export type MeshTopologyShape = {
  uvs: readonly number[];
  vertices: readonly number[];
  triangles: readonly number[];
  hull: number;
  edges?: readonly number[] | undefined;
  mirrorVertexMap?: readonly number[] | undefined;
};

/** The field a topology problem names (`vertices` = the mesh's stored positions). */
export type MeshTopologyField = 'uvs' | 'vertices' | 'triangles' | 'hull' | 'edges' | 'mirrorVertexMap';

type Problem = [field: MeshTopologyField, hint: string] | null;

/** Fewest vertices of a mesh, and the shortest hull (an outline is a polygon). */
const MIN_VERTICES = 3;
/** Most vertices of a mesh: the renderer's 16-bit indices (geometry's RENDER_LAYOUT.MAX_VERTICES). */
const MAX_VERTICES = 65_535;

/** True when every entry of `list` is below `n` (no allocation). */
function allBelow(list: readonly number[], n: number): boolean {
  for (const value of list) if (!(value < n)) return false;
  return true;
}

/** uvs in whole pairs for 3..65,535 vertices; one position pair per uv pair. */
function sizeProblem(t: MeshTopologyShape, n: number): Problem {
  if (t.uvs.length % 2 !== 0 || n < MIN_VERTICES || n > MAX_VERTICES) {
    return ['uvs', `uvs hold 2 numbers (u, v) per vertex and a mesh has ${MIN_VERTICES}..${MAX_VERTICES} vertices (${t.uvs.length} numbers given)`];
  }
  if (t.vertices.length !== t.uvs.length) return ['vertices', `vertices hold one x, y pair per uv pair (${t.vertices.length} numbers for ${t.uvs.length})`];
  return null;
}

/** Whole triangles below the vertex count (none is allowed); a hull of 3..vertex count. */
function shapeProblem(t: MeshTopologyShape, n: number): Problem {
  if (t.triangles.length % 3 !== 0 || !allBelow(t.triangles, n)) return ['triangles', `triangles are index triples below the vertex count (${n})`];
  if (t.hull < MIN_VERTICES || t.hull > n) return ['hull', `hull counts the first outline vertices (${MIN_VERTICES}..${n})`];
  return null;
}

/** Editor-only lists: edge pairs below the vertex count; one mirror entry below the vertex count per vertex. */
function editorProblem(t: MeshTopologyShape, n: number): Problem {
  if (t.edges !== undefined && (t.edges.length % 2 !== 0 || !allBelow(t.edges, n))) return ['edges', `edges are index pairs below the vertex count (${n})`];
  const map = t.mirrorVertexMap;
  if (map !== undefined && (map.length !== n || !allBelow(map, n))) return ['mirrorVertexMap', `mirrorVertexMap holds one counterpart vertex below ${n} per vertex (${map.length} entries)`];
  return null;
}

/**
 * The first rule `t` breaks, as `[field, hint]`, in the order uvs (whole pairs, 3..65,535 vertices), vertices (one
 * x, y pair per uv pair), triangles (whole triples below the vertex count), hull (3..vertex count), edges (pairs below
 * the vertex count), mirrorVertexMap (one entry below the vertex count per vertex); null when every rule holds.
 */
export function meshTopologyProblem(t: MeshTopologyShape): [field: MeshTopologyField, hint: string] | null {
  const n = t.uvs.length / 2;
  return sizeProblem(t, n) ?? shapeProblem(t, n) ?? editorProblem(t, n);
}
